import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { Award, XCircle } from "lucide-react";
import { CertificateShare, CertificateQR, VerifiedBadge } from "@/components/CertificateShare";

export default function VerifyCertificatePage() {
  const { certId } = useParams();
  const [params] = useSearchParams();
  const isEmbed = params.get("embed") === "1";
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/certificates/verify/${certId}`).then((r) => setCert(r.data)).catch(() => setError(true));
  }, [certId]);

  if (isEmbed) {
    // Compact widget layout that fits in a 360x240 iframe
    return (
      <div className="min-h-screen bg-background text-foreground p-4">
        {error && (
          <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-center">
            <XCircle className="mx-auto h-6 w-6 text-destructive" />
            <div className="mt-2 text-sm">Not found</div>
          </div>
        )}
        {cert && (
          <div className="rounded-xl border border-primary/40 bg-gradient-to-br from-primary/10 to-accent/5 p-4 h-full">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <VerifiedBadge />
                <div className="mt-2 font-display text-lg font-bold truncate">{cert.user_name}</div>
                <div className="text-xs text-muted-foreground">completed</div>
                <div className="font-display text-base font-bold truncate">{cert.path_title}</div>
                <div className="mt-2 font-mono text-[0.65rem] text-muted-foreground">{cert.cert_id}</div>
              </div>
              <CertificateQR cert={cert} size={72} />
            </div>
            <a
              href={`${window.location.origin}/verify/${cert.cert_id}`}
              target="_blank"
              rel="noreferrer"
              className="mt-3 block text-center text-[0.65rem] font-mono uppercase tracking-widest text-primary hover:underline"
            >
              Verify at LaneX Academy →
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5">
          <Link to="/" className="flex items-center gap-2">
            <img src="/lanex-mark.png" alt="LaneX Academy" className="h-10 w-auto object-contain bg-white/95 rounded-lg p-1 shadow-md ring-1 ring-primary/30 drop-shadow-[0_0_14px_rgba(0,238,199,0.45)]" />
            <span className="font-display text-lg font-black">LaneX Academy</span>
          </Link>
          <div className="label-mono">// verification</div>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-12 md:py-16">
        {error ? (
          <div className="rounded-2xl border border-destructive/40 bg-destructive/10 p-8 text-center">
            <XCircle className="mx-auto h-10 w-10 text-destructive" />
            <div className="mt-3 font-display text-2xl font-black">Certificate not found</div>
            <div className="mt-1 text-sm text-muted-foreground">The certificate ID {certId} could not be verified.</div>
          </div>
        ) : cert ? (
          <div className="rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 to-accent/5 p-8 md:p-12">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="min-w-0">
                <VerifiedBadge />
                <Award className="mt-6 h-10 w-10 text-primary" />
                <div className="mt-6 label-mono">// certificate of completion</div>
                <div className="mt-2 font-display text-4xl font-black tracking-tighter">{cert.user_name}</div>
                <div className="mt-4 text-sm text-muted-foreground">has successfully completed</div>
                <div className="mt-1 font-display text-2xl font-bold">{cert.path_title}</div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <CertificateQR cert={cert} size={128} />
                <div className="label-mono">scan to verify</div>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-6">
              <div>
                <div className="label-mono">Certificate ID</div>
                <div className="font-mono text-sm">{cert.cert_id}</div>
              </div>
              <div>
                <div className="label-mono">Issued</div>
                <div className="font-mono text-sm">{new Date(cert.issued_at).toLocaleDateString()}</div>
              </div>
            </div>

            <div className="mt-8 border-t border-border pt-6">
              <div className="label-mono mb-3">Share</div>
              <CertificateShare cert={cert} />
            </div>

            <div className="mt-6 rounded-xl border border-border bg-background/50 p-4">
              <div className="label-mono">Employer verification</div>
              <p className="mt-2 text-xs text-muted-foreground">
                This certificate is authentic and can be verified at any time via the QR code or by visiting <span className="font-mono">{window.location.origin}/verify/{cert.cert_id}</span>.
                Employers can embed this verification widget on their site using the &quot;Embed&quot; button above.
              </p>
            </div>
            <div className="mt-3 rounded-xl border border-accent/40 bg-accent/5 p-4">
              <div className="label-mono text-accent">Anti-tamper notice</div>
              <p className="mt-2 text-xs text-muted-foreground">
                The <b>Download PDF</b> button gives you a public copy for offline reference. That PDF can be edited by anyone with the right tools —
                but its legitimacy is proven <b>only</b> by scanning the QR code on it, which must resolve to this verification page
                showing the real recipient, path and issue date. Any tampered PDF fails this check.
              </p>
            </div>
          </div>
        ) : (
          <div className="label-mono animate-pulse">Verifying…</div>
        )}
      </main>
    </div>
  );
}
